import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { CategoryView } from "@/lib/api/types";
import { pickLocalized } from "@/lib/i18n/localized";
import {
  CategoryTile,
  ShareBar,
  CategoryActionsMenu,
} from "@/modules/categories/components/category-ui";
import {
  catalogShare,
  categoryColorIndex,
  categoryTile,
} from "@/modules/categories/lib/category-display";

type CategoryCardProps = {
  locale: Locale;
  category: CategoryView;
  productCount: number;
  totalProducts: number;
  onEdit: () => void;
  onDelete: () => void;
};

export function CategoryCard({
  locale,
  category,
  productCount,
  totalProducts,
  onEdit,
  onDelete,
}: CategoryCardProps) {
  const t = useTranslations();
  const name =
    pickLocalized(locale, category.nameAr, category.nameEn) ??
    category.nameEn ??
    category.nameAr;
  const tile = categoryTile(categoryColorIndex(category.id));
  const share = catalogShare(productCount, totalProducts);
  // The "empty — not shown to buyers" badge must match what actually drives
  // buyer visibility: the backend's ACTIVE-product count (`category.productCount`),
  // not the client-side `productCount` catalog count above (which includes
  // inactive products and is capped at the 200-product fetch window). Only badge
  // when we affirmatively know the active count is 0 — mirrors the buyer's
  // `productCount !== 0` hide predicate, so a stale payload (field absent →
  // undefined) shows no badge rather than mislabelling every category empty.
  const isEmpty = category.productCount === 0;

  return (
    <div className="flex flex-col rounded-[18px] border border-[color:var(--card-br)] bg-[color:var(--card)] p-[15px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-[3px] hover:border-[color:var(--border-strong)] hover:shadow-[0_12px_28px_rgba(0,0,0,0.10)]">
      {/* The badge used to sit inline with the kebab. Its label is a full
          sentence ("Empty — not shown to buyers", and the Arabic is no shorter)
          and it is `whitespace-nowrap`, so it outgrew the card's content box and
          pushed the kebab outside the card border. Give it its own row, where it
          has the full card width. */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <CategoryTile bg={tile.bg} fg={tile.fg} />
        <CategoryActionsMenu onEdit={onEdit} onDelete={onDelete} />
      </div>

      {isEmpty ? (
        <span className="mb-2 inline-flex w-fit max-w-full items-center truncate rounded-full border border-[color:var(--warning-500)]/30 bg-[color:var(--warning-100)] px-2 py-[3px] text-[11px] font-semibold text-[color:var(--warning-700)]">
          {t(keys.categories.emptyBadge)}
        </span>
      ) : null}

      <div className="truncate text-[15.5px] font-bold leading-[1.3] text-[color:var(--text-primary)]">
        {name}
      </div>
      <div className="mt-1 text-[12.5px] text-[color:var(--text-muted)]">
        {t(keys.categories.productsCount, { count: productCount })}
        <span aria-hidden="true" className="px-1.5">
          ·
        </span>
        <span className="font-mono">/{category.slug}</span>
      </div>

      <div className="mt-[15px] border-t border-[color:var(--hair)] pt-[13px]">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[11.5px] font-medium text-[color:var(--text-muted)]">
            {t(keys.categories.catalogShare)}
          </span>
          <span className="text-[12.5px] font-bold text-[color:var(--text-primary)]">
            {share}%
          </span>
        </div>
        <ShareBar pct={share} color={tile.fg} />
      </div>
    </div>
  );
}
